import React, { useEffect, useRef } from "react";
import ReactDOMServer from "react-dom/server";
import { createGlobalStyle } from "styled-components";
import styled from "styled-components";
import Sunburst, { SunburstChartInstance } from "sunburst-chart";
import {
  Color,
  FontFamily,
  FontSize,
  FontWeight,
  Radius,
  Spacing,
} from "@glossy";
import { background } from "@glossy";
import { getOnChangeCanvasCursorEvent } from "@linkfluence/models/events/onChangeCanvasCursor";
import { Locale } from "@linkfluence/models/locale";
import { Metric } from "@linkfluence/models/metric";
import { useSize } from "@lkf/hooks";
import { useComputedSeries } from "../data";
import { Data } from "../model";
import { getTooltipContent, getTooltipTitle } from "../tooltip";

const GlobalStyle = createGlobalStyle`
  .sunburst-viz text {
    font-family: ${FontFamily.Sans};
    font-size: ${FontSize.Small};
    font-weight: ${FontWeight.Normal};
    fill: ${Color.White};
  }
  .sunburst-viz text .text-contour {
    fill: none;
    stroke: none;
  }
  .sunburst-tooltip {
    background: ${Color.BlackPearl};
    padding: ${Spacing.MediumLarge};
    border-radius: ${Radius.Medium};
    font-family: ${FontFamily.Sans};
    font-size: ${FontSize.Medium};
    z-index: 999;
  }

  .sunburst-viz .main-arc {
    stroke: ${background};
  }
  .text-contour {
    stroke: none !important;
  }
`;

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  cursor: pointer;

  .slice path:hover {
    cursor: var(--cursor-magic-wand);
  }
`;

export interface IProps {
  series: Data;
  metric: Metric;
  locale: Locale;
  onClickSlice?: (id: string) => void;
  disableAnimation?: boolean;
}

const SunburstChart = ({
  series,
  onClickSlice,
  metric,
  locale,
  disableAnimation = false,
}: IProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const chart = useRef<SunburstChartInstance>(null);
  const { width, height } = useSize(ref);
  const computedSeries = useComputedSeries(series);
  useEffect(() => {
    if (ref.current && !chart.current) {
      chart.current = Sunburst()
        .excludeRoot(true)
        .radiusScaleExponent(0.7)
        .centerRadius(0.4)
        .labelOrientation("auto")
        .label("label")
        .color("color")
        .size("vizSize")
        .transitionDuration(disableAnimation ? 0 : 800)
        .tooltipTitle((node) =>
          ReactDOMServer.renderToString(getTooltipTitle(node))
        )
        .tooltipContent((node) => getTooltipContent(node, metric, locale))
        .onClick((node) => node && onClickSlice(node.uid))(ref.current)
        .onHover((node: Node) => {
          if (onClickSlice) {
            const cursor = node ? "var(--cursor-magic-wand)" : "default";
            // Use ref instead of event.target because this version of sunburst-chart does not pass event to onHover
            if (ref.current.style.cursor !== cursor) {
              ref.current.style.cursor = cursor;
              ref.current.dispatchEvent(getOnChangeCanvasCursorEvent());
            }
          }
        });
    }
  }, [
    ref,
    computedSeries,
    metric,
    onClickSlice,
    height,
    locale,
    disableAnimation,
  ]);

  useEffect(() => {
    if (chart.current) {
      chart.current.width(width).data(computedSeries).height(height);
    }
  }, [chart, width, computedSeries, height]);

  return (
    <>
      <GlobalStyle />
      <ChartWrapper ref={ref} />
    </>
  );
};

export default SunburstChart;
